import { ArticleCrudData, ArticleService, InventoryPool } from 'admin/articleService'
import { InventoryService } from 'inventory/inventoryService'
import { OptionsService } from 'products/options/optionsService'
import { ArticleFromApi } from 'products/types'
import { CapacityReportService, GetCapacityReportResponseData } from 'orders/capacityReport/capacityReportService'
import { CapacityDetails, CapacityHealth, CapacitySummary } from 'capacity/types'
import {
    CapacityGateway,
    CapacityMember,
    CapacityMemberCounts,
    CapacityMemberLink,
    CapacityMembers,
    CapacityNotSupportedError,
    FailedOp,
    GatewayCapabilities,
    OptionItemsCoverage,
    ProductOp,
    SavePlan,
    SaveResult,
    splitVisitorCapacities,
    TodayUsage,
    VISITOR_KINDS,
    VisitorCapacities,
    VisitorKind,
} from './capacityGateway'
import { t } from 'translations'

export const OPTION_ITEMS_CAP = 80
const OPTION_ITEMS_CONCURRENCY = 6
const PRODUCT_WRITE_CONCURRENCY = 3

export interface ClientSideGatewayDeps {
    inventoryService: Pick<
        InventoryService,
        'getInventoryList' | 'getInventoryDetails' | 'createInventoryItem' | 'updateInventoryItem'
    >
    articleService: Pick<ArticleService, 'fetchArticle' | 'updateArticle'>
    capacityReportService: Pick<CapacityReportService, 'getCapacityReport'>
    loadArticles: (accountSlug: string) => Promise<ArticleFromApi[]>
    loadOptionItems: (accountSlug: string) => Promise<OptionItemSweep>
}

export const CLIENT_SIDE_CAPABILITIES: GatewayCapabilities = {
    list: 'reservationsV1',
    members: 'clientJoin',
    optionItemsCap: OPTION_ITEMS_CAP,
    atomicSave: false,
    delete: false,
    usage: 'peakSlice',
    health: 'none',
    visitors: 'byRoleOrName',
    timeSlots: 'clientResolver',
}

export const VISITOR_CAPACITY_NAMES: Record<VisitorKind, string> = {
    dated: 'Dated park visitors',
    undated: 'Undated park visitors',
}
const VISITOR_ROLES: Record<VisitorKind, string> = { dated: 'admission', undated: 'undatedadmission' }

export const visitorKindOf = (capacity: Pick<CapacitySummary, 'name' | 'role'>): VisitorKind | null => {
    const role = (capacity.role ?? '').toLowerCase().replace(/_/g, '')
    for (const kind of VISITOR_KINDS) {
        if (role === VISITOR_ROLES[kind] || capacity.name.trim() === VISITOR_CAPACITY_NAMES[kind]) {
            return kind
        }
    }
    return null
}

export async function mapWithConcurrency<T, R>(
    items: T[],
    limit: number,
    task: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
    const results: R[] = new Array(items.length)
    let next = 0
    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (next < items.length) {
            const index = next++
            results[index] = await task(items[index], index)
        }
    })
    await Promise.all(workers)
    return results
}

export interface OptionItemPair {
    groupUuid: string
    groupName: string
    itemId: string
}

export type OptionItemDetails = Awaited<ReturnType<OptionsService['getOptionItemDetails']>>

export interface OptionItemSweep {
    items: { pair: OptionItemPair; item: OptionItemDetails }[]
    coverage: OptionItemsCoverage
}

export async function sweepOptionItems(
    optionsService: Pick<OptionsService, 'getOptionGroups' | 'getOptionItemDetails'>,
    accountSlug: string,
): Promise<OptionItemSweep> {
    try {
        const groups = await optionsService.getOptionGroups(accountSlug)
        const pairs: OptionItemPair[] = groups.flatMap((group) =>
            group.optionItems.map((item) => ({ groupUuid: group.uuid, groupName: group.name, itemId: item.id })),
        )
        if (pairs.length > OPTION_ITEMS_CAP) {
            return { items: [], coverage: 'capped' }
        }
        const details = await mapWithConcurrency(pairs, OPTION_ITEMS_CONCURRENCY, (pair) =>
            optionsService
                .getOptionItemDetails(accountSlug, pair.itemId, pair.groupUuid)
                .then((item) => ({ pair, item }))
                .catch(() => null),
        )
        const items = details.filter((entry): entry is OptionItemSweep['items'][number] => !!entry)
        return { items, coverage: items.length < details.length ? 'failed' : 'full' }
    } catch {
        return { items: [], coverage: 'failed' }
    }
}

export const articleName = (article: Pick<ArticleFromApi, 'id' | 'name'>): string =>
    article.name?.['en'] || Object.values(article.name ?? {})[0] || article.id

const linksFor = (resources: InventoryPool[] | undefined, capacityId: string): CapacityMemberLink[] =>
    (resources ?? [])
        .filter((resource) => resource.poolId === capacityId)
        .map((resource) => ({ productListId: resource.productListId ?? null, amountUsed: resource.amountUsed ?? 1 }))

const sameLinks = (a: CapacityMemberLink[], b: CapacityMemberLink[]): boolean => {
    if (a.length !== b.length) {
        return false
    }
    const key = (link: CapacityMemberLink) => `${link.productListId ?? ''}:${link.amountUsed}`
    const sortedA = a.map(key).sort()
    const sortedB = b.map(key).sort()
    return sortedA.every((value, index) => value === sortedB[index])
}

const sameResources = (a: InventoryPool[], b: InventoryPool[]): boolean => JSON.stringify(a) === JSON.stringify(b)

const errorMessage = (error: unknown): string =>
    error instanceof Error && error.message ? error.message : t('capacity.products.somethingWentWrong')

export const applyProductOp = (current: InventoryPool[], capacityId: string, op: ProductOp): InventoryPool[] => {
    switch (op.kind) {
        case 'add': {
            const alreadyLinked = current.some((resource) => resource.poolId === capacityId && !resource.productListId)
            if (alreadyLinked) {
                return current
            }
            return [...current, { poolId: capacityId, amountUsed: op.amountUsed ?? 1 }]
        }
        case 'remove':
            return current.filter((resource) => resource.poolId !== capacityId)
        case 'setAmount':
            return current.map((resource) =>
                resource.poolId === capacityId && !resource.productListId
                    ? { ...resource, amountUsed: op.amountUsed ?? resource.amountUsed }
                    : resource,
            )
        default:
            return current
    }
}

export const peakSlice = (
    report: GetCapacityReportResponseData,
    capacityId: string,
    basis: TodayUsage['basis'] = 'peakSlice',
): TodayUsage => {
    const slices = (report.availability || [])
        .filter((entry) => entry.resourceId === capacityId)
        .reduce<{ available: number | null; capacity: number | null }[]>(
            (all, entry) => [...all, ...(entry.availability || [])],
            [],
        )
    let peak: { limit: number; used: number; left: number } | null = null
    for (const slice of slices) {
        if (slice.capacity === null || slice.capacity === undefined) {
            continue
        }
        const available = slice.available === null || slice.available === undefined ? slice.capacity : slice.available
        const used = Math.max(0, slice.capacity - available)
        const left = Math.max(0, available)
        if (!peak || used > peak.used || (used === peak.used && left < peak.left)) {
            peak = { limit: slice.capacity, used, left }
        }
    }
    return {
        status: 'ready',
        limit: peak?.limit ?? null,
        used: peak?.used ?? null,
        left: peak?.left ?? null,
        basis,
        timezone: report.timezone,
    }
}

export class ClientSideCapacityGateway implements CapacityGateway {
    readonly capabilities = CLIENT_SIDE_CAPABILITIES

    constructor(private deps: ClientSideGatewayDeps) {}

    listCapacities(accountSlug: string): Promise<CapacitySummary[]> {
        return this.deps.inventoryService.getInventoryList(accountSlug)
    }

    async getCapacity(accountSlug: string, capacityId: string): Promise<CapacityDetails> {
        const details = await this.deps.inventoryService.getInventoryDetails(accountSlug, capacityId)
        return { ...details, availabilityRules: details.availabilityRules ?? [] }
    }

    visitorKindOf(capacity: CapacitySummary): VisitorKind | null {
        return visitorKindOf(capacity)
    }

    async getVisitorCapacities(accountSlug: string): Promise<VisitorCapacities> {
        return splitVisitorCapacities(await this.listCapacities(accountSlug), visitorKindOf).visitors
    }

    async createVisitorCapacity(accountSlug: string, kind: VisitorKind): Promise<CapacitySummary> {
        const existing = (await this.getVisitorCapacities(accountSlug))[kind]
        if (existing) {
            return existing
        }
        return this.deps.inventoryService.createInventoryItem(accountSlug, {
            account: accountSlug,
            name: VISITOR_CAPACITY_NAMES[kind],
            defaultAvailability: null,
            // Pythia counts visitors only from Venue pools, whatever the kind; the type cannot change afterwards.
            reservationPeriodType: 'Venue',
        })
    }

    async getMembers(accountSlug: string, capacityId: string): Promise<CapacityMembers> {
        const articles = await this.deps.loadArticles(accountSlug)
        const products: CapacityMember[] = articles
            .filter((article) => article.resources.some((resource) => resource.inventoryPoolId === capacityId))
            .map((article) => ({
                kind: 'product',
                id: article.id,
                name: articleName(article),
                businessType: article.businessType ?? null,
                isBundle: !!article.isBundle,
                categoryIds: article.categories.map((category) => category.id),
                inactiveCategoryIds: (article.inactiveCategories ?? []).map((category) => category.id),
                links: article.resources
                    .filter((resource) => resource.inventoryPoolId === capacityId)
                    .map((resource) => ({
                        productListId: resource.categoryId ?? null,
                        amountUsed: resource.amountUsed ?? 1,
                    })),
            }))
        const sweep = await this.deps.loadOptionItems(accountSlug)
        const optionItems: CapacityMember[] = []
        for (const entry of sweep.items) {
            const links = linksFor(entry.item.usedResources, capacityId)
            if (links.length === 0) {
                continue
            }
            optionItems.push({
                kind: 'optionItem',
                id: entry.item.id,
                name: entry.item.name,
                businessType: 'upsell',
                isBundle: false,
                categoryIds: [],
                inactiveCategoryIds: [],
                links,
                optionGroup: { uuid: entry.pair.groupUuid, name: entry.pair.groupName },
            })
        }
        return { capacityId, members: [...products, ...optionItems], optionItems: sweep.coverage }
    }

    async getMemberCounts(accountSlug: string): Promise<CapacityMemberCounts> {
        const [articles, sweep] = await Promise.all([
            this.deps.loadArticles(accountSlug),
            this.deps.loadOptionItems(accountSlug),
        ])
        const counts: CapacityMemberCounts['counts'] = {}
        const bump = (capacityId: string, key: 'products' | 'optionItems') => {
            counts[capacityId] = counts[capacityId] ?? { products: 0, optionItems: 0 }
            counts[capacityId][key] += 1
        }
        for (const article of articles) {
            const capacityIds = new Set(article.resources.map((resource) => resource.inventoryPoolId))
            capacityIds.forEach((capacityId) => bump(capacityId, 'products'))
        }
        for (const entry of sweep.items) {
            const capacityIds = new Set((entry.item.usedResources ?? []).map((resource) => resource.poolId))
            capacityIds.forEach((capacityId) => bump(capacityId, 'optionItems'))
        }
        return { counts, optionItems: sweep.coverage }
    }

    async save(accountSlug: string, plan: SavePlan): Promise<SaveResult> {
        let capacityId = plan.capacityId
        let created = false
        if (plan.capacity) {
            try {
                if (plan.capacity.kind === 'create') {
                    const saved = await this.deps.inventoryService.createInventoryItem(accountSlug, plan.capacity.body)
                    capacityId = saved.id
                    created = true
                } else {
                    await this.deps.inventoryService.updateInventoryItem(accountSlug, plan.capacity.body)
                }
            } catch (error) {
                return {
                    capacityId: plan.capacityId,
                    created: false,
                    capacitySaved: false,
                    succeeded: [],
                    failed: plan.productOps.map((op) => ({ op, message: errorMessage(error) })),
                }
            }
        }
        if (!capacityId) {
            return {
                capacityId: null,
                created,
                capacitySaved: true,
                succeeded: [],
                failed: plan.productOps.map((op) => ({ op, message: t('capacity.products.noCapacityToAddTo') })),
            }
        }
        const targetId = capacityId
        const opsByProduct = new Map<string, ProductOp[]>()
        for (const op of plan.productOps) {
            opsByProduct.set(op.productId, [...(opsByProduct.get(op.productId) ?? []), op])
        }
        const errors = new Map<ProductOp, string>()
        await mapWithConcurrency(Array.from(opsByProduct.values()), PRODUCT_WRITE_CONCURRENCY, async (ops) => {
            try {
                await this.applyOps(accountSlug, targetId, ops)
            } catch (error) {
                ops.forEach((op) => errors.set(op, errorMessage(error)))
            }
        })
        const failed: FailedOp[] = plan.productOps
            .filter((op) => errors.has(op))
            .map((op) => ({ op, message: errors.get(op) as string }))
        return {
            capacityId,
            created,
            capacitySaved: true,
            succeeded: plan.productOps.filter((op) => !errors.has(op)),
            failed,
        }
    }

    private async applyOps(accountSlug: string, capacityId: string, ops: ProductOp[]): Promise<void> {
        const article: ArticleCrudData = await this.deps.articleService.fetchArticle(accountSlug, ops[0].productId)
        const current = article.usedResources ?? []
        const currentLinks = linksFor(current, capacityId)
        if (ops.some((op) => op.expectedLinks && !sameLinks(currentLinks, op.expectedLinks))) {
            throw new Error(t('capacity.products.changedElsewhere'))
        }
        const next = ops.reduce((resources, op) => applyProductOp(resources, capacityId, op), current)
        if (sameResources(next, current)) {
            return
        }
        await this.deps.articleService.updateArticle(accountSlug, { ...article, usedResources: next }, ops[0].productId)
    }

    async deleteCapacity(): Promise<void> {
        throw new CapacityNotSupportedError('BUIL-800', 'Capacities cannot be deleted yet.')
    }

    async getTodayUsage(accountSlug: string, capacityIds: string[], date: string): Promise<Record<string, TodayUsage>> {
        if (capacityIds.length === 0) {
            return {}
        }
        try {
            const report = await this.deps.capacityReportService.getCapacityReport({
                accountSlug,
                date,
                resourceIds: capacityIds.join(','),
            })
            return capacityIds.reduce<Record<string, TodayUsage>>((all, id) => {
                all[id] = peakSlice(report, id)
                return all
            }, {})
        } catch {
            return capacityIds.reduce<Record<string, TodayUsage>>((all, id) => {
                all[id] = { status: 'unavailable', limit: null, used: null, left: null, basis: 'peakSlice' }
                return all
            }, {})
        }
    }

    getHealth(): CapacityHealth {
        return 'unknown'
    }
}
